import { useEffect, useMemo, useRef } from "react"

import {
  getMessageInterval,
  getMessageStartSeconds,
} from "@/components/transcript/TimestampDisplay"
import type { Message } from "@/types/Transcript"

import { EVENTS } from "./event-bus"
import { useEventBus } from "./useEventBus"

/** Media seek positions can truncate transcript timestamps to whole microseconds. */
const MEDIA_TIME_PRECISION_SECONDS = 0.000001

/**
 * Follows the player on the bus: selects the transcript message being spoken as
 * the audio plays or seeks. A message selected on the bus holds until playback
 * leaves it; where selecting a message also seeks the player, the follow waits
 * for that seek to land first, and where it does not, the selection holds until
 * the player's next seek.
 */
export function useTranscriptAudioSelection({
  transcript,
  simulationOutputId,
  audioDuration,
  audioIntervals,
  onSelect,
  selectionSeeksPlayer = true,
}: {
  transcript?: Message[] | null
  simulationOutputId?: string
  audioDuration?: number | null
  audioIntervals?: Array<{ start: number; end: number } | null>
  onSelect: (index: number | null) => void
  /** Whether the host's player seeks to a message selected on the bus. */
  selectionSeeksPlayer?: boolean
}) {
  const starts = useMemo(
    () =>
      (transcript ?? []).map(
        (message, index) =>
          audioIntervals?.[index]?.start ?? getMessageStartSeconds(message),
      ),
    [transcript, audioIntervals],
  )
  const activeIndex = useRef<number | null>(null)
  const pendingSeek = useRef<{ start: number } | null>(null)

  const explicitInterval = useRef<{ start: number; end: number } | null>(null)
  // A bus selection the player does not seek to holds until the next seek, so
  // playback elsewhere does not take it straight back.
  const heldUntilSeek = useRef(false)
  const settledSeekTime = useRef<number | null>(null)
  const onSelectRef = useRef(onSelect)
  const previousResult = useRef(simulationOutputId)
  useEffect(() => {
    onSelectRef.current = onSelect
  }, [onSelect])
  useEffect(() => {
    if (previousResult.current === simulationOutputId) {
      return
    }
    previousResult.current = simulationOutputId
    heldUntilSeek.current = false
    activeIndex.current = null
    pendingSeek.current = null
    settledSeekTime.current = null
    explicitInterval.current = null
    onSelectRef.current(null)
  }, [simulationOutputId])

  useEventBus(EVENTS.MESSAGE_SELECTED, ({ messageIndex }) => {
    const index =
      messageIndex !== null && messageIndex >= 0 && messageIndex < starts.length
        ? messageIndex
        : null
    activeIndex.current = index
    settledSeekTime.current = null
    explicitInterval.current =
      index === null || !transcript?.[index]
        ? null
        : (audioIntervals?.[index] ?? getMessageInterval(transcript[index]))
    const start = index === null ? null : starts[index]
    pendingSeek.current =
      selectionSeeksPlayer &&
      index !== null &&
      start != null &&
      Number.isFinite(start) &&
      start >= 0
        ? { start }
        : null
    heldUntilSeek.current = !selectionSeeksPlayer && index !== null
    onSelect(index)
  })

  useEventBus(EVENTS.AUDIO_SEEK, (payload) => {
    if (
      payload.simulationOutputId !== undefined &&
      payload.simulationOutputId !== simulationOutputId
    ) {
      return
    }
    pendingSeek.current = null
    settledSeekTime.current = null
    explicitInterval.current = null
    heldUntilSeek.current = false
  })

  useEventBus(EVENTS.AUDIO_TIME, (payload) => {
    if (
      !Number.isFinite(payload.time) ||
      (payload.simulationOutputId !== undefined &&
        payload.simulationOutputId !== simulationOutputId)
    ) {
      return
    }
    if (heldUntilSeek.current) {
      if (payload.seekTarget === undefined) {
        return
      }
      heldUntilSeek.current = false
    }
    const pending = pendingSeek.current
    if (pending) {
      if (payload.seekTarget !== undefined) {
        if (payload.seekTarget !== pending.start) {
          return
        }
      } else {
        const earliest =
          audioDuration != null && audioDuration > 0
            ? Math.min(pending.start, audioDuration)
            : pending.start
        if (
          payload.time + MEDIA_TIME_PRECISION_SECONDS < earliest ||
          payload.time - MEDIA_TIME_PRECISION_SECONDS > pending.start
        ) {
          return
        }
      }
      pendingSeek.current = null
      settledSeekTime.current = payload.time
      return
    }
    if (
      settledSeekTime.current !== null &&
      Math.abs(settledSeekTime.current - payload.time) <=
        MEDIA_TIME_PRECISION_SECONDS
    ) {
      return
    }
    settledSeekTime.current = null
    const interval = explicitInterval.current
    if (
      interval &&
      payload.time + MEDIA_TIME_PRECISION_SECONDS >= interval.start &&
      payload.time - MEDIA_TIME_PRECISION_SECONDS <= interval.end
    ) {
      return
    }
    explicitInterval.current = null
    const activeStart =
      activeIndex.current === null ? null : starts[activeIndex.current]
    if (
      activeStart != null &&
      Math.abs(activeStart - payload.time) <= MEDIA_TIME_PRECISION_SECONDS
    ) {
      return
    }
    let nextIndex: number | null = null
    let latestStart = -Infinity
    starts.forEach((start, index) => {
      if (
        start != null &&
        Number.isFinite(start) &&
        start >= 0 &&
        start <= payload.time + MEDIA_TIME_PRECISION_SECONDS &&
        start >= latestStart
      ) {
        latestStart = start
        nextIndex = index
      }
    })
    if (nextIndex === activeIndex.current) {
      return
    }
    activeIndex.current = nextIndex
    if (nextIndex !== null) {
      onSelect(nextIndex)
    }
  })
}
